/**
 * The vendor page's reviews and recommendations (docs/MarketplaceDesign.md
 * sections 3.7 and 3.8; M3): verified reviews with the vendor's public
 * responses, the members who recommend the vendor (with "May earn a referral
 * reward" where that is so, CR-36), a recommend toggle for members, a
 * response form for the vendor and hide or restore for org admins, who hide
 * only for a published reason (CR-35).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as marketApi from '../api/marketplace';
import { useToast } from './toast';
import { Spinner, ErrorAlert, SelectField, TextareaField, formatDate } from './ui';
import { HIDE_REASON_LABELS, RESPONSE_TEXT_MAX, ratingLabel } from '../lib/marketplace';
import { cn, ui } from '../lib/ui';

function Stars({ rating }) {
  return (
    <span aria-label={`${rating} of 5 stars`} role="img" className="text-dcon-accent">
      {'★'.repeat(rating)}
      <span className="text-dcon-ink-subtle">{'★'.repeat(5 - rating)}</span>
    </span>
  );
}

function ReviewItem({ review, canRespond, canModerate, hideReasons, onChanged }) {
  const toast = useToast();
  const [mode, setMode] = useState(null);
  const [text, setText] = useState(review.response?.text || '');
  const [reason, setReason] = useState(hideReasons[0]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  async function run(fn, done) {
    setBusy(true);
    try {
      await fn();
      toast.success(done);
      setMode(null);
      onChanged();
    } catch (err) {
      toast.error(err.message || 'Nothing changed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className={cn(ui.cardFlat, 'mb-3')}>
      <div className={cn(ui.row, ui.spread)}>
        <span>
          <Stars rating={review.rating} /> <strong>{review.authorName}</strong>
          <span className={cn(ui.pillSuccess, 'ml-2')}>Verified customer</span>
          {review.status === 'hidden' && (
            <span className={cn(ui.pillWarning, 'ml-2')}>
              Hidden: {HIDE_REASON_LABELS[review.hiddenReason] || review.hiddenReason}
            </span>
          )}
        </span>
        <span className={ui.hint}>
          {formatDate(review.createdAt)}
          {review.editedAt ? ' (edited)' : ''}
        </span>
      </div>
      <p className={cn(ui.hint, 'mt-1 mb-1')}>{review.serviceName}</p>
      {review.text && <p className="mt-0 whitespace-pre-line">{review.text}</p>}
      {review.response && (
        <blockquote className="m-0 border-l-4 border-z-line pl-3 text-body-s">
          <strong>Response from the vendor:</strong> {review.response.text}
        </blockquote>
      )}
      {mode === 'respond' && (
        <div className="mt-3">
          <TextareaField
            label="Your public response"
            rows={3}
            maxLength={RESPONSE_TEXT_MAX}
            value={text}
            onChange={e => setText(e.target.value)}
          />
          <div className={ui.row}>
            <button
              type="button"
              className={cn(ui.btnPrimary, ui.btnSm)}
              disabled={busy || !text.trim()}
              onClick={() =>
                run(
                  () => marketApi.respondToReview(review.reviewId, text.trim()),
                  'Response posted'
                )
              }
            >
              Post response
            </button>
            <button
              type="button"
              className={cn(ui.btnGhost, ui.btnSm)}
              onClick={() => setMode(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {mode === 'hide' && (
        <div className="mt-3">
          <SelectField
            label="Reason"
            value={reason}
            onChange={e => setReason(e.target.value)}
            hint="A review is never hidden for being negative. The author is told the reason."
          >
            {hideReasons.map(r => (
              <option key={r} value={r}>
                {HIDE_REASON_LABELS[r] || r}
              </option>
            ))}
          </SelectField>
          <TextareaField
            label="Note to the author (optional)"
            rows={2}
            value={note}
            onChange={e => setNote(e.target.value)}
          />
          <div className={ui.row}>
            <button
              type="button"
              className={cn(ui.btnDanger, ui.btnSm)}
              disabled={busy}
              onClick={() =>
                run(
                  () =>
                    marketApi.moderateReview(
                      review.reviewId,
                      'hide',
                      reason,
                      note.trim() || undefined
                    ),
                  'Review hidden'
                )
              }
            >
              Hide review
            </button>
            <button
              type="button"
              className={cn(ui.btnGhost, ui.btnSm)}
              onClick={() => setMode(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {!mode && (canRespond || canModerate) && (
        <div className={cn(ui.row, 'mt-2')}>
          {canRespond && (
            <button
              type="button"
              className={cn(ui.btnSecondary, ui.btnSm)}
              onClick={() => setMode('respond')}
            >
              {review.response ? 'Edit response' : 'Respond'}
            </button>
          )}
          {canModerate &&
            (review.status === 'hidden' ? (
              <button
                type="button"
                className={cn(ui.btnGhost, ui.btnSm)}
                disabled={busy}
                onClick={() =>
                  run(() => marketApi.moderateReview(review.reviewId, 'restore'), 'Review restored')
                }
              >
                Restore
              </button>
            ) : (
              <button
                type="button"
                className={cn(ui.btnGhost, ui.btnSm)}
                onClick={() => setMode('hide')}
              >
                Hide
              </button>
            ))}
        </div>
      )}
    </li>
  );
}

export default function VendorReviews({ vendor, isOwner }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await marketApi.listReviews(vendor.vendorId));
    } catch (err) {
      setError(err);
    }
  }, [vendor.vendorId]);

  useEffect(() => {
    load();
  }, [load]);

  async function toggleRecommend() {
    setBusy(true);
    try {
      if (data.iRecommend) await marketApi.unrecommendVendor(vendor.vendorId);
      else await marketApi.recommendVendor(vendor.vendorId);
      toast.success(
        data.iRecommend ? 'Recommendation removed' : `You recommend ${vendor.businessName}`
      );
      load();
    } catch (err) {
      toast.error(err.message || 'Nothing changed');
    } finally {
      setBusy(false);
    }
  }

  if (error) return <ErrorAlert error={error} />;
  if (!data) return <Spinner label="Loading reviews..." />;
  const mayEarn = data.recommendedBy.some(r => r.mayEarn);
  return (
    <>
      <section aria-labelledby="reviews-heading" className="mt-6">
        <h2 id="reviews-heading" className={ui.h2}>
          Reviews <span className={cn(ui.hint, 'font-normal')}>{ratingLabel(data.summary)}</span>
        </h2>
        <p className={cn(ui.hint, 'mt-0')}>
          Only members who bought from this vendor through the marketplace can review it.
        </p>
        {data.reviews.length === 0 ? (
          <p className={ui.muted}>No reviews yet.</p>
        ) : (
          <ul className="m-0 list-none p-0">
            {data.reviews.map(r => (
              <ReviewItem
                key={`${r.reviewId}-${r.status}-${r.response?.text || ''}`}
                review={r}
                canRespond={data.canRespond}
                canModerate={data.canModerate}
                hideReasons={data.hideReasons}
                onChanged={load}
              />
            ))}
          </ul>
        )}
      </section>
      <section aria-labelledby="recommended-heading" className="mt-6">
        <h2 id="recommended-heading" className={ui.h2}>
          Recommended by
        </h2>
        {data.recommendedBy.length === 0 ? (
          <p className={ui.muted}>No member recommends this vendor yet.</p>
        ) : (
          <ul className="m-0 mb-3 list-none p-0">
            {data.recommendedBy.map(r => (
              <li key={r.userId}>
                <Link to={`/members/${encodeURIComponent(r.userId)}`}>{r.userName}</Link>
              </li>
            ))}
          </ul>
        )}
        {mayEarn && (
          <p className={cn(ui.alertInfo, 'mb-3')}>
            Members who recommend {vendor.businessName} may earn a referral reward.
          </p>
        )}
        {!isOwner && (
          <button
            type="button"
            className={cn(ui.btnSecondary, ui.btnSm)}
            disabled={busy}
            onClick={toggleRecommend}
          >
            {data.iRecommend ? 'Stop recommending' : `Recommend ${vendor.businessName}`}
          </button>
        )}
      </section>
    </>
  );
}
